/**
 * useRemoteSelections Hook - 整合 Presence 與 CodeMirror 遠端選取高亮
 *
 * 將 RoomParticipant 資料轉換為 RemoteSelectionData 並更新 EditorView
 *
 * @module @linkode/web/editor/cm/useRemoteSelections
 * @see SPEC-007 Requirements 6.3
 */

import { useEffect, useCallback, useRef } from 'react';
import type { EditorView } from '@codemirror/view';
import type { RoomParticipant, Selection } from '@linkode/shared';
import { updateRemoteSelections, type RemoteSelectionData } from './selectionHighlight';
import { offsetToPosition } from './remoteCursor';

// ============================================================================
// Types
// ============================================================================

export interface UseRemoteSelectionsOptions {
  /** EditorView instance (can be null during initialization) */
  view: EditorView | null;
  /** Remote participants from usePresence hook */
  participants: RoomParticipant[];
  /** Debounce interval for selection updates (ms) */
  debounceMs?: number;
}

export interface UseRemoteSelectionsResult {
  /** Convert current editor selection to Selection for presence update */
  getLocalSelection: () => Selection | null;
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook to synchronize remote participant selections with CodeMirror editor
 *
 * @param options - Hook options
 * @returns Helper functions for local selection
 *
 * @example
 * ```tsx
 * const { getLocalSelection } = useRemoteSelections({
 *   view: editorView,
 *   participants,
 * });
 *
 * // When local selection changes, update presence
 * const handleSelectionChange = () => {
 *   const selection = getLocalSelection();
 *   updateSelection(selection);
 * };
 * ```
 */
export function useRemoteSelections({
  view,
  participants,
  debounceMs = 50,
}: UseRemoteSelectionsOptions): UseRemoteSelectionsResult {
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update remote selections when participants change
  useEffect(() => {
    if (!view) return;

    // Clear any pending update
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    // Debounce the update to avoid excessive re-renders
    updateTimeoutRef.current = setTimeout(() => {
      const selectionData: RemoteSelectionData[] = participants
        .filter((p) => p.presence.selection !== null)
        .map((p) => ({
          userId: p.userId,
          selection: p.presence.selection,
          colorId: p.presence.colorId,
        }));

      updateRemoteSelections(view, selectionData);
    }, debounceMs);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [view, participants, debounceMs]);

  // Clear selections when view is destroyed
  useEffect(() => {
    return () => {
      if (view) {
        try {
          updateRemoteSelections(view, []);
        } catch {
          // View may be destroyed, ignore
        }
      }
    };
  }, [view]);

  // Get local selection from editor
  const getLocalSelection = useCallback((): Selection | null => {
    if (!view) return null;

    const { anchor, head } = view.state.selection.main;

    // No selection if anchor equals head
    if (anchor === head) return null;

    return {
      anchor: offsetToPosition(view, anchor),
      head: offsetToPosition(view, head),
    };
  }, [view]);

  return {
    getLocalSelection,
  };
}

export default useRemoteSelections;
